import { DealerAssociationResponse } from 'services/dealer-association';
import { DealerAssociation } from 'services/dealer-association/types';
import {
  AccountPreference,
  Address,
  DeliveryMethodConfig,
  DropBoxAddress,
  Fulfillment,
  Preferences,
  StoreLocation,
  UserPreferencesResponseV2Array,
  UserPreferencesResponseV3
} from 'services/userPreferences/types';
import { INVALID_ADDRESSES } from '../address-book/_constants/constants';
import {
  AddressBookAddressType,
  AddressBookType
} from 'services/address-book/types';
import { cloneDeep } from 'lodash';
import {
  FULFILLMENT_TYPE_DELIVERY,
  FULFILLMENT_TYPE_DROPBOX,
  FULFILLMENT_TYPE_PICKUP,
  FULFILLMENT_TYPE_SHOP_ONLINE
} from './constants/constants';

export const getAccountPreferences = (
  shoppingPreference: UserPreferencesResponseV2Array
): AccountPreference => {
  return Array.isArray(shoppingPreference?.accountPreferences) &&
    shoppingPreference?.accountPreferences?.length > 0
    ? shoppingPreference?.accountPreferences[0]
    : ({} as AccountPreference);
};

export const getAssociationPreferences = (
  dealerAssociation: DealerAssociationResponse
): DealerAssociation => {
  return Array.isArray(dealerAssociation?.associations) &&
    dealerAssociation?.associations.length > 0
    ? getDefault(dealerAssociation?.associations)
    : ({} as DealerAssociation);
};

const buildFulfillment = (
  type: 'pickup' | 'delivery' | 'dropBox',
  preferences: AccountPreference,
  deliveryAddressId?: string,
  dropboxAddress?: DropBoxAddress[]
) => {
  const pref = preferences?.[type] ?? ({} as DeliveryMethodConfig);

  const base = {
    specialInstructions: pref?.specialInstructions ?? '',
    instructionCode: getDefaultValue(pref?.instructions) ?? '',
    instructionLabel: getDefaultName(pref?.instructions, 'displayName') ?? '',
    methodCode: getDefaultValue(pref?.methods) ?? '',
    methodLabel:
      getDefaultName(getMethodDisplayName(pref?.methods), 'displayName') ?? ''
  };

  if (type === FULFILLMENT_TYPE_DELIVERY) {
    return {
      ...base,
      addressId: pref?.address?.id ?? deliveryAddressId
    };
  }

  if (type === FULFILLMENT_TYPE_DROPBOX) {
    const selectedDropBoxAddress = dropboxAddress?.find(
      (item) => item?.line1?.trim() === pref?.address?.line1?.trim()
    );
    const { city = '', state = '' } = selectedDropBoxAddress || {};
    const prefAddress = {
      ...selectedDropBoxAddress,
      line4: city,
      line5: state
    };
    return {
      ...base,
      address: prefAddress ?? ({} as Address)
    };
  }

  return base;
};

export const getFinalizedFulfillment = (
  deliveryPreference: string,
  accountPreferences: AccountPreference,
  deliveryAddress: AddressBookAddressType[],
  isDeliveryEnabled: boolean,
  isDropBoxEnabled: boolean,
  dropboxAddress: DropBoxAddress[]
) => {
  const availabilityMap = {
    delivery: isDeliveryEnabled,
    shopOnline: isDeliveryEnabled,
    dropBox: isDropBoxEnabled
  };
  const deliveryPreferences = accountPreferences?.deliveryPreferences ?? [];
  // Default address id from address book api
  const deliveryAddressId = deliveryAddress?.find(
    (addr) => addr?.isDefault
  )?.id;
  const isSelectedMethodUnavailable = deliveryPreferences?.some(
    (item) =>
      item?.value === deliveryPreference &&
      !availabilityMap[item?.value] &&
      item?.isDefault
  );

  return {
    pickup:
      deliveryPreference === FULFILLMENT_TYPE_PICKUP ||
      isSelectedMethodUnavailable
        ? buildFulfillment(FULFILLMENT_TYPE_PICKUP, accountPreferences)
        : null,
    delivery:
      deliveryPreference === FULFILLMENT_TYPE_DELIVERY ||
      deliveryPreference === FULFILLMENT_TYPE_SHOP_ONLINE
        ? buildFulfillment(
            FULFILLMENT_TYPE_DELIVERY,
            accountPreferences,
            deliveryAddressId
          )
        : null,
    dropBox:
      deliveryPreference === FULFILLMENT_TYPE_DROPBOX
        ? buildFulfillment(
            FULFILLMENT_TYPE_DROPBOX,
            accountPreferences,
            '',
            dropboxAddress
          )
        : null
  };
};

export const getFulfillmentType = (fulfillment: Fulfillment): string => {
  if (fulfillment?.pickup) {
    return FULFILLMENT_TYPE_PICKUP;
  } else if (fulfillment?.delivery) {
    return FULFILLMENT_TYPE_DELIVERY;
  } else if (fulfillment?.dropBox) {
    return FULFILLMENT_TYPE_DROPBOX;
  }
  return '';
};

export const getInitialFormValues = (
  shoppingPreference: UserPreferencesResponseV2Array,
  shoppingPreferenceV3: UserPreferencesResponseV3,
  dealerAssociationResponse: DealerAssociationResponse,
  addressBook: AddressBookType,
  isDeliveryEnabled: boolean,
  isDropBoxEnabled: boolean,
  dropboxAddress: DropBoxAddress[]
) => {
  const accountPreferences = getAccountPreferences(shoppingPreference);
  const dealerPreferences = getAssociationPreferences(
    dealerAssociationResponse
  );
  const deliveryPreference =
    getDefaultValue(accountPreferences?.deliveryPreferences) ?? '';

  const { shippingAddresses = [], shippingAndBillingAddresses = [] } =
    addressBook ?? {};

  const deliveryAddress = mergeAndSortAllAddresses({
    shippingAddresses,
    shippingAndBillingAddresses
  });

  const initialValues = {
    ...shoppingPreferenceV3,
    customerNumber:
      getDefaultValue(dealerPreferences?.customerNumbersInfo) ?? '',
    customerName:
      getDefaultName(dealerPreferences?.customerNumbersInfo, 'name')?.split(
        '-'
      )[1] ?? '',
    storeLocationCode:
      getDefaultStoreLocationId(
        accountPreferences?.storeLocationInformation,
        shoppingPreferenceV3?.storeLocationCode
      ) ?? '',
    languageId: getDefaultValue(shoppingPreference?.languagePreferences) ?? '',
    paymentMethodId: getDefaultValue(shoppingPreference?.paymentMethods) ?? '',
    startPage: getDefaultValue(shoppingPreference?.startPages) ?? '',
    fulfillment: getFinalizedFulfillment(
      deliveryPreference,
      accountPreferences,
      deliveryAddress,
      isDeliveryEnabled,
      isDropBoxEnabled,
      dropboxAddress
    )
  };
  return initialValues;
};

const getDefault = (arr) =>
  arr?.length > 1
    ? (arr || [])?.find((i) => i?.isDefault) || {}
    : arr?.[0] || {};

export const getDefaultLocation = (arr: StoreLocation[], id: string) =>
  (arr || [])?.find((i) => i?.value === id);

export const getDefaultStoreLocationId = (arr: StoreLocation[], id: string) =>
  getDefaultLocation(arr, id)?.value;
export const getDefaultName = (arr, name) => getDefault(arr)?.[name];
export const getDefaultValue = (arr) => getDefault(arr)?.value;
export const getDefaultDisplayName = (arr) => getDefault(arr)?.displayName;

type sortAllAddressesProps = {
  shippingAddresses: AddressBookAddressType[];
  shippingAndBillingAddresses: AddressBookAddressType[];
};
export const mergeAndSortAllAddresses = ({
  shippingAddresses,
  shippingAndBillingAddresses
}: sortAllAddressesProps) => {
  const allAddressesArray = cloneDeep([
    ...shippingAddresses,
    ...shippingAndBillingAddresses
  ]);

  allAddressesArray?.sort((addressA, addressB) => {
    const a = addressA?.nickname.toUpperCase();
    const b = addressB?.nickname.toUpperCase();

    if (addressA?.isDefault !== addressB?.isDefault) {
      return addressA?.isDefault ? -1 : 1;
    }
    const c = a < b ? -1 : 0;
    return a > b ? 1 : c;
  });

  return allAddressesArray?.filter(
    (address) => !address?.nickname.includes(INVALID_ADDRESSES.JOBSITE)
  );
};

export const getMethodDisplayName = (methods: Preferences[]) =>
  methods?.map((item) => ({
    ...item,
    displayName: item?.displayName?.includes('|')
      ? item?.displayName?.split('|')[1]
      : item?.displayName || ''
  }));
